import React, { useState, useEffect } from "react";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Breadcrumb from "../../../../components/layout/Breadcrumb";
import {
  createTicket,
  clearCreateState,
  fetchAllCategories,
  attachTicketDoc,
} from "../../../../redux/slice/supportSlice";
import { toast, ToastContainer } from "react-toastify";

export default function CreateTicket() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});

  const {
    createLoading: loading,
    createSuccess,
    createError: error,
    categories = [],
  } = useSelector((state) => state.support);

  /* STATE ================= */
  const [formData, setFormData] = useState({
    issueTitle: "",
    issueDescription: "",
    category: "",
    priority: "",
    evidence: null,
    profileFields: [],
  });

  /* EFFECTS ================= */
  useEffect(() => {
    dispatch(fetchAllCategories());
  }, [dispatch]);

  /* OPTIONS ================= */
  const profileOptions = [
    //{ value: "DOCUMENT_UPLOAD", label: "Document Upload" },
    { value: "PERSONAL_INFORMATION", label: "Personal Information" },
    { value: "BANK_DETAILS", label: "Bank Details" },
    { value: "INSURANCE_DEPENDENT", label: "Insurance Dependent" },
    { value: "NOMINEE", label: "Nominee" },
    { value: "EDUCATION", label: "Education" },
    { value: "EXPERIENCE", label: "Experience" },
    //{ value: "ADDRESS", label: "Address" },
    //{ value: "PROFESSIONAL_REFERENCE", label: "Professional Reference" },
    { value: "ADDITIONAL_DETAILS", label: "Additional Details" },
  ];

  /* HANDLERS ================= */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    if (name === "category") {
      setFormData((prev) => ({
        ...prev,
        category: value,
        profileFields: [],
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleMultiSelect = (selectedOptions) => {
    setErrors((prev) => ({
      ...prev,
      profileFields: "",
    }));

    setFormData((prev) => ({
      ...prev,
      profileFields: selectedOptions || [],
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    //const MIN_SIZE = 10 * 1024; // 10KB
    const MAX_SIZE = 10 * 1024 * 1024; // 5MB

    if (file.size < MIN_SIZE || file.size > MAX_SIZE) {
      toast.error("File size is invalid. Size must be between 10KB and 5MB!", {
        className: "toast-error-custom",
      });
      e.target.value = null;
      setFormData((prev) => ({ ...prev, evidence: null }));
      return;
    }

    setFormData((prev) => ({ ...prev, evidence: file }));
  };

  /* SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    let newErrors = {};

    if (!formData.issueTitle.trim()) {
      newErrors.issueTitle = "Please enter issue title";
    }

    if (!formData.category) {
      newErrors.category = "Please select category";
    }

    if (!formData.priority) {
      newErrors.priority = "Please select priority";
    }

    if (!formData.issueDescription.trim()) {
      newErrors.issueDescription = "Please enter issue description";
    }

    if (
      formData.category === "PROFILE_UPDATE" &&
      formData.profileFields.length === 0
    ) {
      newErrors.profileFields = "Please select at least one sub category";
    }
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    // SUB CATEGORY REQUIRED VALIDATION
    if (
      formData.category === "PROFILE_UPDATE" &&
      formData.profileFields.length === 0
    ) {
      toast.error("Please select at least one sub category!", { className: "toast-error-custom", });
      return;
    }
    const payload = {
      title: formData.issueTitle,
      description: formData.issueDescription,
      category: formData.category,
      priority: formData.priority,
      subCategories:
        formData.category === "PROFILE_UPDATE"
          ? formData.profileFields.map((item) => item.value)
          : [],
    };
    try {
      const result = await dispatch(createTicket(payload)).unwrap();
      const ticketId = result?.id;

      if (ticketId && formData.evidence) {
        const uploadedData = new FormData();
        uploadedData.append("file", formData.evidence);
        const mimeType = formData.evidence.type;
        const detectedExtension = mimeType ? mimeType.split('/').pop() : "unknown";
        uploadedData.append("fileType", detectedExtension);
        await dispatch(attachTicketDoc({ id: ticketId, formData: uploadedData })).unwrap();
        toast.success("Ticket and documents submitted successfully!", { className: 'toast-success-custom' });
      } else {
        toast.success("Ticket created successfully!");
      }
      setFormData({
        issueTitle: "",
        issueDescription: "",
        category: "",
        priority: "",
        evidence: null,
        profileFields: [],
      });

      dispatch(clearCreateState());
      setTimeout(() => {
        navigate("/admin/support");
      }, 2000);
    } catch (err) {
      toast.error(`${err?.data?.reason || "Unable to connect to server"}`, { className: "toast-error-custom", });
    }
  };

  return (
    <div className="container-fluid">
      <div className=" d-flex justify-content-between align-items-center" style={{ flexDirection: "row-reverse", marginBottom: "18px" }}>
        <div className="icons-list">
          <Breadcrumb />
        </div>
      </div>

      <div className="ticket-container d-flex justify-content-between align-items-top">
        <button type="button" className="btn btn-link text-decoration-none mb-3 ps-0 fw-medium primary-btn" onClick={() => navigate("/admin/support")}>
          ← Back to tickets
        </button>

        <div className="ticket-form-wrapper">
          <div className="form-header">
            <div className="title">
              <h1 className="name">Create New Ticket</h1>
              <p>Submit your issue and our HR support team will assist you shortly.</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="ticket-form">
            {/* TITLE */}
            <div className="form-group">
              <label className="form-label">
                Issue Title <span className="required">*</span>
              </label>
              <div className="input-section">
                <input
                  type="text"
                  name="issueTitle"
                  value={formData.issueTitle}
                  onChange={handleChange}
                  placeholder="Title of your issue"
                  className="form-input"
                />
              </div>
              {errors.issueTitle && (
                <div className="userErrorMessage">{errors.issueTitle}</div>
              )}
            </div>

            {/* CATEGORY + PRIORITY */}
            <div className="form-row">
              <div className="form-group half">
                <label className="form-label">
                  Category <span className="required">*</span>
                </label>
                <div className="input-section">
                  <select name="category" value={formData.category} onChange={handleChange} className="form-select" >
                    <option value="">-- Select Category --</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.name}>
                        {cat.name
                          .toLowerCase()
                          .replaceAll("_", " ")
                          .replace(/\b\w/g, (c) => c.toUpperCase())}
                      </option>
                    ))}
                  </select>
                </div>
                {errors.category && (
                  <div className="userErrorMessage">{errors.category}</div>
                )}
              </div>

              <div className="form-group half">
                <label className="form-label">
                  Priority <span className="required">*</span>
                </label>
                <div className="input-section">
                  <select className="form-select" name="priority" value={formData.priority} onChange={handleChange} >
                    <option value="">-- Select Priority --</option>
                    <option value="LOW">Low</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="HIGH">High</option>
                  </select>
                </div>
                {errors.priority && (
                  <div className="userErrorMessage">{errors.priority}</div>
                )}
              </div>
            </div>

            {/* PROFILE UPDATE MULTI SELECT */}
            {formData.category === "PROFILE_UPDATE" && (
              <div className="form-group">
                <label className="form-label">
                  Select Profile Update Fields{" "}
                  <span className="required">*</span>
                </label>
                <div className="input-section">
                  <Select className="profile-form-select"
                    options={profileOptions}
                    isMulti
                    value={formData.profileFields}
                    onChange={handleMultiSelect}
                    placeholder="Search & select fields..."
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        padding: "5px 10px",
                        border: "2px solid transparent",
                        borderRadius: "5px",
                        backgroundImage: "linear-gradient(#FFFFFF, #FFFFFF), linear-gradient(to bottom, #5A5A5A, #ca10cb)",
                        backgroundClip: "padding-box, border-box",
                        backgroundOrigin: "border-box",
                        fontSize: "14px",
                        minHeight: "40px",
                        boxShadow: "none",

                        "&:hover": {
                          border: "2px solid transparent",
                        },
                      }),

                      multiValue: (base) => ({
                        ...base,
                        background: "#f3e8ff",
                        borderRadius: "4px",
                        padding: "2px 4px",
                      }),

                      multiValueLabel: (base) => ({
                        ...base,
                        color: "#512F9F",
                        fontWeight: 500,
                      }),

                      multiValueRemove: (base) => ({
                        ...base,
                        color: "#512F9F",
                        cursor: "pointer",

                        ":hover": {
                          backgroundColor: "#512F9F",
                          color: "#fff",
                        },
                      }),
                    }}
                  />
                </div>
                {errors.profileFields && (
                  <div className="userErrorMessage">{errors.profileFields}</div>
                )}
              </div>
            )}

            {/* DESCRIPTION */}
            <div className="form-group">
              <label className="form-label">
                Issue Description <span className="required">*</span>
              </label>
              <textarea className="form-textarea" rows="4" placeholder="Provide detailed information..." name="issueDescription"                
                value={formData.issueDescription}
                onChange={handleChange} />
              {errors.issueDescription && (
                <div className="userErrorMessage">{errors.issueDescription}</div>
              )}
            </div>

            {/* FILE */}
            <div className="form-group">
              <label className="form-label">
                Upload File{" "}
                <span className="optional">(Optional)</span>
              </label>
              <small className="help-text">
                Required Format: PDF, PNG, JPG, or JPEG | Size: 10MB
              </small>
              <input
                type="file"
                name="evidence"
                onChange={handleFileChange}
                accept="image/*,.pdf,.doc,.docx,.png"
                className="form-control mt-2"
              />
            </div>

            {/* ERROR */}
            {error && <p style={{ color: "red" }}>{error}</p>}

            {/* SUBMIT */}
            <div className="form-footer">
              <button type="submit" className="submit-btn primary-btn" disabled={loading}>
                {loading ? "Submitting..." : "Submit Ticket"}
              </button>
            </div>
          </form>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
}